import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { BLOG_POSTS, BLOG_CATEGORIES } from "../lib/serviceCatalog";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import StickyCTA from "../sections/StickyCTA";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag } from "lucide-react";
import { BRAND } from "../lib/constants";

export function BlogIndexPage() {
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    document.title = "Senior Care Blog & Resources | Madison Peaceful Path Home Care";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Helpful resources for Madison families: dementia support, home safety, aging in place, and senior care tips from local experts.");
  }, []);

  const visible = filter === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === filter);

  return (
    <div data-testid="blog-index" className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-12 bg-gradient-to-br from-[#005165] to-[#003946] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C4B088] mb-4">Resources & Insights</p>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.05]">
            Guidance for Madison families
          </h1>
          <p className="mt-5 text-white/80 max-w-2xl text-lg">
            Honest, useful articles on aging well at home, navigating dementia, and supporting the
            ones who care for our loved ones.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-2 mb-10">
            {["All", ...BLOG_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`blog-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`px-4 h-9 rounded-full text-sm border transition-colors ${
                  filter === c
                    ? "bg-[#005165] text-white border-[#005165]"
                    : "bg-white text-[#1F2937] border-[#E5DDC9] hover:border-[#AC9667]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <p className="text-[#4B5563] py-12 text-center">More posts in this category coming soon. <Link to="/#contact" className="text-[#AC9667] underline">Have a question now?</Link></p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((p) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.slug}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block bg-white rounded-3xl overflow-hidden border border-[#E5DDC9] hover:shadow-[0_20px_40px_-20px_rgba(0,81,101,0.25)] transition-shadow"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-[#4B5563] mb-3">
                      <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3 text-[#AC9667]" /> {p.category}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readTime}</span>
                    </div>
                    <h2 className="font-serif text-2xl text-[#005165] leading-tight mb-2">{p.title}</h2>
                    <p className="text-sm text-[#4B5563] leading-relaxed">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1 mt-4 text-sm text-[#AC9667] group-hover:gap-2 transition-all">Read more <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Madison Peaceful Path Home Care`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", post.excerpt);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center">
          <p className="font-serif text-3xl text-[#005165] mb-3">Post not found</p>
          <Link to="/blog" className="text-[#AC9667] underline">← Back to blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`blog-post-${slug}`} className="min-h-screen bg-white">
      <Header />

      <article className="pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <button onClick={() => navigate("/blog")} className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#AC9667] mb-6">
            <ArrowLeft className="w-4 h-4" /> All posts
          </button>
          <div className="flex items-center gap-3 text-xs text-[#4B5563] mb-4">
            <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3 text-[#AC9667]" /> {post.category}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#005165] leading-[1.08] mb-6">{post.title}</h1>
          <div className="aspect-[16/9] rounded-3xl overflow-hidden mb-10">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
          <div className="prose prose-lg max-w-none">
            {post.body.map((p, i) => (
              <p key={i} className="text-[17px] text-[#1F2937] leading-relaxed mb-5"
                dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#005165]">$1</strong>') }}
              />
            ))}
          </div>

          <div className="mt-12 p-7 rounded-3xl bg-gradient-to-br from-[#005165] to-[#003946] text-white">
            <p className="font-serif text-2xl mb-3">Need to talk to a real person?</p>
            <p className="text-white/80 mb-5">We offer free, no-obligation in-home consultations in the Madison area.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/#contact"><Button className="bg-[#AC9667] hover:bg-[#7A6A48] text-white rounded-full h-12 px-6">Schedule Consultation</Button></Link>
              <a href={`tel:${BRAND.phoneRaw}`} className="inline-flex items-center text-sm text-[#C4B088] hover:text-white px-3 h-12">Or call {BRAND.phone}</a>
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <StickyCTA />
    </div>
  );
}
